/**
 * Training Context (Issue #2679, #3225)
 *
 * Provides training state management for the command-based training system.
 * Tracks the currently active training item (first incomplete item in user's plan)
 * and exposes functions to check if hints should be shown for specific items.
 *
 * This replaces the spotlight-tour approach with a "guided doing" approach
 * where users learn by performing actions with contextual hints.
 */

'use client'

import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { SHELL_ENDPOINTS, shellRead } from '@/lib/shell-transport'
import { useFailOpenContext } from '@/contexts/use-fail-open-context'
import { reportClientError } from '@/lib/client-error'
import { ERROR_IDS } from '@/lib/errors/constants'

/** Auto-clear review state after this many milliseconds */
const REVIEW_TIMEOUT_MS = 30_000

/**
 * Response shape of `GET /api/shell/training-hints`.
 *
 * Mirrors what the two underlying actions return, so the provider keeps
 * treating a failed read exactly as it did when it called them directly:
 * no active item, hints not hidden.
 */
type TrainingHintsPayload = {
  activeItem:
    | { success: true; data: ActiveTrainingItem | null }
    | { success: false; error?: string }
  // NOT `{ success: true; ... }`. `getHideTrainingHints` is a `publicAction`,
  // and `publicAction` widens its return to `R | ActionFailure`, so the route
  // can legitimately emit `{ success: false }`. Typed as the literal `true`,
  // TypeScript narrowed the runtime check below to always-true and made the
  // failure branch look dead to every future reader (#13434 review).
  hideHints:
    | { success: true; hidden: boolean }
    | { success: false; error?: string }
}

/**
 * Active training item data from the server.
 */
export interface ActiveTrainingItem {
  id: string
  title: string
  description: string
  startPath: string | null
  hintText: string | null
}

/**
 * Training context value type.
 */
interface TrainingContextValue {
  /** The currently active training item (first incomplete in user's plan) */
  activeItem: ActiveTrainingItem | null
  /** The item currently being reviewed (Issue #3225) */
  reviewingItem: ActiveTrainingItem | null
  /** Whether the context is still loading the active item */
  isLoading: boolean
  /** Check if a training item should show its hint */
  isActive: (itemId: string) => boolean
  /** Refresh the active item (e.g., after completing an item) */
  refresh: () => Promise<void>
  /** Start reviewing a completed training item (Issue #3225) */
  startReview: (item: ActiveTrainingItem) => void
  /** Whether the user has disabled hint popovers */
  hideTrainingHints: boolean
}

const TrainingContext = createContext<TrainingContextValue | undefined>(
  undefined,
)

export { TrainingContext }

/**
 * Safe default for `useTrainingContext` when no provider is mounted
 * (#5860). Behaves as if the user has no active training item: no
 * hints fire, isActive is always false, refresh/startReview are
 * no-ops. Pages render normally; the missing-provider log fires
 * once for observability.
 */
const TRAINING_FAIL_OPEN_DEFAULT: TrainingContextValue = {
  activeItem: null,
  reviewingItem: null,
  isLoading: false,
  isActive: () => false,
  refresh: async () => {},
  startReview: () => {},
  hideTrainingHints: false,
}

/**
 * Training provider component.
 *
 * Fetches the user's active training item on mount and provides
 * context for training hints throughout the app.
 */
export function TrainingProvider({ children }: { children: ReactNode }) {
  const [activeItem, setActiveItem] = useState<ActiveTrainingItem | null>(null)
  const [reviewingItem, setReviewingItem] = useState<ActiveTrainingItem | null>(
    null,
  )
  const [isLoading, setIsLoading] = useState(true)
  const [hideTrainingHints, setHideTrainingHints] = useState(false)
  const reviewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearReviewTimer = useCallback(() => {
    if (reviewTimerRef.current) {
      clearTimeout(reviewTimerRef.current)
      reviewTimerRef.current = null
    }
  }, [])

  const fetchActiveItem = useCallback(async () => {
    try {
      setIsLoading(true)
      // Over HTTP, not as two server actions (#13434). This provider is
      // mounted in the root layout, so its mount effect used to put two
      // entries in the App Router's action queue on every route — where a
      // queued action outliving a navigation resolves the router back to the
      // address the user left. See `src/lib/shell-transport.ts`. Both reads
      // are served by one round trip now, which is also half the burst.
      const payload = await shellRead<TrainingHintsPayload>(
        SHELL_ENDPOINTS.trainingHints,
      )
      // `shellRead` returns `null` rather than throwing — on a non-OK status,
      // on a transport failure, and on a malformed body alike. So the `catch`
      // below can no longer be entered by any of the failures this reporting
      // was written for (#4xxx), and a 403 or a 500 would otherwise degrade
      // silently to "no active item". Report from here instead, matching
      // `ai-chat-provider.tsx`, which throws into its own catch for the same
      // reason. The state fallback is unchanged: this provider has always
      // rendered as "no active item, hints not hidden" on a failed read.
      if (payload === null) {
        reportClientError({
          errorId: ERROR_IDS.TRAINING_CONTEXT_FETCH_ACTIVE_ITEM_FAILED,
          action: 'fetchActiveItem',
          error: new Error(
            `shellRead returned null for ${SHELL_ENDPOINTS.trainingHints}`,
          ),
        })
        setActiveItem(null)
        setHideTrainingHints(false)
        return
      }

      const result = payload.activeItem
      const hideHintsResult = payload.hideHints

      setActiveItem(result?.success ? (result.data ?? null) : null)
      setHideTrainingHints(
        hideHintsResult?.success ? hideHintsResult.hidden : false,
      )
    } catch (error) {
      reportClientError({
        errorId: ERROR_IDS.TRAINING_CONTEXT_FETCH_ACTIVE_ITEM_FAILED,
        action: 'fetchActiveItem',
        error,
      })
      setActiveItem(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch active item on mount
  useEffect(() => {
    fetchActiveItem()
  }, [fetchActiveItem])

  // Cleanup review timer on unmount
  useEffect(() => {
    return () => clearReviewTimer()
  }, [clearReviewTimer])

  /**
   * Check if a training item should show its hint.
   * Returns true if the item is the active item or the reviewing item.
   */
  const isActive = useCallback(
    (itemId: string): boolean => {
      return activeItem?.id === itemId || reviewingItem?.id === itemId
    },
    [activeItem, reviewingItem],
  )

  /**
   * Refresh the active item (e.g., after completing an item).
   * Also clears review state.
   */
  const refresh = useCallback(async () => {
    clearReviewTimer()
    setReviewingItem(null)
    await fetchActiveItem()
  }, [fetchActiveItem, clearReviewTimer])

  /**
   * Start reviewing a completed training item (Issue #3225).
   * Sets the reviewing item so isActive() returns true for it.
   * Auto-clears after REVIEW_TIMEOUT_MS.
   */
  const startReview = useCallback(
    (item: ActiveTrainingItem) => {
      clearReviewTimer()
      setReviewingItem(item)
      reviewTimerRef.current = setTimeout(() => {
        setReviewingItem(null)
        reviewTimerRef.current = null
      }, REVIEW_TIMEOUT_MS)
    },
    [clearReviewTimer],
  )

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      activeItem,
      reviewingItem,
      isLoading,
      isActive,
      refresh,
      startReview,
      hideTrainingHints,
    }),
    [
      activeItem,
      reviewingItem,
      isLoading,
      isActive,
      refresh,
      startReview,
      hideTrainingHints,
    ],
  )

  return <TrainingContext value={contextValue}>{children}</TrainingContext>
}

/**
 * Hook to access the training context.
 *
 * Fail-open (#5860): if no `TrainingProvider` is mounted the hook
 * returns a safe default (no active item, no hints) and emits a
 * one-shot structured log so misconfigurations remain observable.
 *
 * @example
 * ```tsx
 * const { isActive, activeItem } = useTrainingContext()
 *
 * if (isActive('updates.post_first_update')) {
 *   // Show hint for posting first update
 * }
 * ```
 */
export function useTrainingContext(): TrainingContextValue {
  return useFailOpenContext(TrainingContext, {
    hookName: 'useTrainingContext',
    errorId: ERROR_IDS.CONTEXT_TRAINING_PROVIDER_MISSING,
    defaultValue: TRAINING_FAIL_OPEN_DEFAULT,
  })
}
